export interface WizardData {
  income: number
  targetRent: number
  hasAval: string
  jobTenure: string
  dicom: string
  fullName: string
  email: string
  rut: string
}

export interface ScoreResult {
  score: number
  probability: 'Alta' | 'Media' | 'Baja'
  diagnosis: string
  recommendation: string
  actions: string[]
  miniScores: {
    incomeVsRent: 'Fuerte' | 'Medio' | 'Débil'
    stability: 'Fuerte' | 'Medio' | 'Débil'
    aval: 'Fuerte' | 'Medio' | 'Débil'
    risk: 'Fuerte' | 'Medio' | 'Débil'
    validation: 'Fuerte'
  }
}

export function calculateScore(data: WizardData): ScoreResult {
  let score = 0

  // 1. Ingreso vs arriendo (40 points)
  const ratio = data.targetRent / data.income
  let incomeVsRentScore = 0
  let incomeVsRentLabel: 'Fuerte' | 'Medio' | 'Débil' = 'Débil'

  if (ratio <= 0.25) {
    incomeVsRentScore = 40
    incomeVsRentLabel = 'Fuerte'
  } else if (ratio <= 0.3) {
    incomeVsRentScore = 35
    incomeVsRentLabel = 'Fuerte'
  } else if (ratio <= 0.35) {
    incomeVsRentScore = 25
    incomeVsRentLabel = 'Medio'
  } else if (ratio <= 0.4) {
    incomeVsRentScore = 15
    incomeVsRentLabel = 'Medio'
  } else {
    incomeVsRentScore = 5
    incomeVsRentLabel = 'Débil'
  }
  score += incomeVsRentScore

  // 2. Estabilidad laboral (20 points)
  let stabilityScore = 0
  let stabilityLabel: 'Fuerte' | 'Medio' | 'Débil' = 'Débil'

  if (data.jobTenure === 'Más de 2 años') {
    stabilityScore = 20
    stabilityLabel = 'Fuerte'
  } else if (data.jobTenure === '1 a 2 años') {
    stabilityScore = 15
    stabilityLabel = 'Fuerte'
  } else if (data.jobTenure === '6 a 12 meses') {
    stabilityScore = 10
    stabilityLabel = 'Medio'
  } else {
    stabilityScore = 4
    stabilityLabel = 'Débil'
  }
  score += stabilityScore

  // 3. Aval (20 points)
  let avalScore = 0
  let avalLabel: 'Fuerte' | 'Medio' | 'Débil' = 'Débil'

  if (data.hasAval === 'Sí') {
    avalScore = 20
    avalLabel = 'Fuerte'
  } else {
    avalScore = 0
    avalLabel = 'Débil'
  }
  score += avalScore

  // 4. Riesgo financiero (10 points)
  let riskScore = 0
  let riskLabel: 'Fuerte' | 'Medio' | 'Débil' = 'Débil'

  if (data.dicom === 'No') {
    riskScore = 10
    riskLabel = 'Fuerte'
  } else if (data.dicom === 'No estoy seguro') {
    riskScore = 5
    riskLabel = 'Medio'
  } else {
    riskScore = 0
    riskLabel = 'Débil'
  }
  score += riskScore

  // 5. Validación (10 points) — always 10 because they validated identity
  score += 10

  // Determine probability
  let probability: 'Alta' | 'Media' | 'Baja' = 'Baja'
  if (score >= 80) probability = 'Alta'
  else if (score >= 60) probability = 'Media'

  // Generate diagnosis and recommendation
  let diagnosis = ''
  let recommendation = ''
  const actions: string[] = []

  const formatCLP = (val: number) =>
    '$' + new Intl.NumberFormat('es-CL').format(Math.round(val))

  if (probability === 'Alta') {
    diagnosis = 'Tu perfil es competitivo'
    recommendation = 'Estás en buena posición. Postula con confianza.'
    actions.push('Postula con confianza a propiedades dentro de tu rango')
    if (avalLabel === 'Débil') {
      actions.push('Agregar aval te haría aún más fuerte')
    }
    actions.push('Prioriza propiedades donde puedas demostrar estabilidad')
  } else if (probability === 'Media') {
    diagnosis = 'Tienes opciones, pero con limitaciones'
    recommendation =
      'No es por falta de opciones. Es por cómo estás postulando.'
    if (incomeVsRentLabel === 'Débil' || incomeVsRentLabel === 'Medio') {
      actions.push(`Busca arriendos bajo ${formatCLP(data.income * 0.3)}`)
    }
    if (avalLabel === 'Débil') {
      actions.push('Consigue un aval — es tu mayor debilidad')
    }
    if (stabilityLabel === 'Débil') {
      actions.push('Espera a tener más antigüedad laboral')
    }
    actions.push('Prioriza propiedades con menos requisitos')
  } else {
    diagnosis = 'Hoy tienes baja probabilidad de que te acepten'
    recommendation =
      'No es por falta de opciones. Es por cómo estás postulando.'
    if (riskLabel === 'Débil') {
      actions.push('Regulariza tu situación financiera (DICOM)')
    }
    if (incomeVsRentLabel === 'Débil') {
      actions.push(`Busca arriendos bajo ${formatCLP(data.income * 0.3)}`)
    }
    if (avalLabel === 'Débil') {
      actions.push('Agregar un aval cambia completamente tus opciones')
    }
    if (stabilityLabel === 'Débil') {
      actions.push('Busca estabilizar tu situación laboral primero')
    }
    if (actions.length === 0) {
      actions.push('Mejora tu perfil antes de seguir postulando')
    }
  }

  return {
    score,
    probability,
    diagnosis,
    recommendation,
    actions,
    miniScores: {
      incomeVsRent: incomeVsRentLabel,
      stability: stabilityLabel,
      aval: avalLabel,
      risk: riskLabel,
      validation: 'Fuerte',
    },
  }
}
